import React, { PropsWithChildren } from 'react';
import { Flag, useFlags } from 'hooks/useFlags';
import Spinner from '../Spinner';
import { Route, routes } from 'routing/routes';
import { Navigate } from 'react-router-dom';

const DEFAULT_SPINNER = <Spinner width={26} height={26} color="white" />;

const DEFAULT_FALLBACK: FallbackStrategy = {
  type: 'ignore',
} as const;

/**
 * Renders its children based on a feature flag. Renders `else` prop if
 * the feature flag is not rendered.
 * Renders `null` while waiting for the feature flag provider to load
 */
export function FeatureFlaggedRoute(
  props: PropsWithChildren<
    Omit<FeatureFlaggedProps, 'fallbackStrategy'> & { else?: React.ReactElement }
  >,
) {
  return (
    <FeatureFlagged
      {...props}
      fallbackStrategy={
        props.else
          ? {
              type: 'render',
              element: props.else,
            }
          : {
              type: 'redirect',
              page: routes[404](),
            }
      }
    />
  );
}

/**
 * Blocks the rendering of children until a flag can be confirmed to be enabled
 * @param children
 * @param flag
 * @param spinner
 * @param fallbackStrategy
 * @constructor
 */
export function FeatureFlagged({
  children,
  flag,
  spinner,
  fallbackStrategy = DEFAULT_FALLBACK,
}: PropsWithChildren<FeatureFlaggedProps>) {
  const { isReady, isFlagEnabled } = useFlags();

  if (!isReady) {
    // we specifically want to have a way to render a null value
    // for when we don't want to display anything
    if (spinner === null) {
      return null;
    }
    return spinner ?? DEFAULT_SPINNER;
  }

  const shouldFallback = flag instanceof Function ? !flag(isFlagEnabled) : !isFlagEnabled(flag);

  if (shouldFallback) {
    switch (fallbackStrategy.type) {
      case 'render':
        return fallbackStrategy.element;
      case 'redirect':
        return <Navigate to={fallbackStrategy.page} replace />;
      case 'ignore':
        return null;
    }
  }

  return <>{children}</>;
}

type FallbackStrategy =
  | {
      type: 'redirect';
      page: Route;
    }
  | {
      type: 'ignore';
    }
  | {
      type: 'render';
      element: React.ReactElement;
    };

export type IsFlagEnabled = (flag: Flag) => boolean;

export type FeatureFlaggedProps = {
  /**
   * Only renders when the given flag is enabled by default.
   * Also allows a function input * for more complex
   * flag checks (like negative conditions)
   */
  flag: Flag | ((isFlagEnabled: IsFlagEnabled) => boolean);
  spinner?: React.ReactElement | null;
  fallbackStrategy?: FallbackStrategy;
};
